import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import PropTypes from 'prop-types';
import useMetronomeEngine from '../../../../hooks/useMetronomeEngine';
import { useMetronomeStore } from '../plugins/MetronomePlugin/MetronomePlugin';
import { getSetting, settingKeys } from '../../../../utils/settings';

import './SuperMetronome.scss';

const SuperMetronome = ({ song }) => {
  const { t } = useTranslation();
  // Start hidden: matches SuperScroll — the next-song button is on screen until
  // the observer proves otherwise, avoiding a flash of the control on first paint.
  const [hidden, setHidden] = useState(true);
  const metronomeIdRef = useRef(`super-metronome-${song?._id || 'compact'}-${Math.random()}`);

  const engine = useMetronomeEngine(metronomeIdRef.current);

  // Read play state from the shared store (not local state) so this stays in
  // sync with the metronome plugin in ViewHeader — whichever control actually
  // started playback owns the worker, but both should reflect it as playing.
  const activeMetronomeId = useMetronomeStore((state) => state.activeMetronomeId);
  const activeBpm = useMetronomeStore((state) => state.activeBpm);
  const globalIsFlashing = useMetronomeStore((state) => state.isFlashing);
  const stopAllMetronomes = useMetronomeStore((state) => state.stopAllMetronomes);

  const bpm = song?.bpm;
  const isDisabled = !bpm || bpm <= 0;
  const isPlaying = activeMetronomeId !== null;

  useEffect(() => {
    const target = document.querySelector('.ViewNextSongBtn');
    if (!target) return;

    const observer = new IntersectionObserver(([entry]) => setHidden(entry.isIntersecting), { threshold: 0 });

    observer.observe(target);
    return () => observer.disconnect();
  }, [song?._id]);

  // Keep BPM/time signature in sync while playing as the song changes
  useEffect(() => {
    if (isPlaying && bpm > 0) {
      engine.updateBPM(bpm, song?.timeSignature || '4/4');
    }
  }, [bpm, isPlaying, song?.timeSignature, engine]);

  if (!getSetting(settingKeys.superMetronome)) return null;

  const handleToggle = () => {
    if (isDisabled) return;

    if (isPlaying) {
      stopAllMetronomes();
    } else {
      engine.start(bpm, song?.timeSignature || '4/4');
    }
  };

  const bpmToShow = activeBpm || bpm || '--';

  const overlayBtnClasses = cx('overlay-btn', {
    playing: isPlaying,
    flashing: globalIsFlashing,
    disabled: isDisabled,
  });

  const overlayBtnTitle = isDisabled
    ? t('view.noBpm')
    : t(isPlaying ? 'view.stopMetronomeAt' : 'view.startMetronomeAt', { bpm: isPlaying ? activeBpm : bpm });

  return (
    <div className={`SuperMetronome ${hidden ? 'hidden' : ''}`}>
      <button className={overlayBtnClasses} onClick={handleToggle} title={overlayBtnTitle}>
        {bpmToShow}
      </button>
    </div>
  );
};

SuperMetronome.propTypes = {
  song: PropTypes.object,
};

export default SuperMetronome;
